import { useEffect, useRef, useState, type ReactNode } from 'react';
import { gsap } from 'gsap';
import { Icon } from '../../components/Icon';
import { Wordmark } from '../../components/Logo';
import { Go, SiteHeader } from './SiteHeader';
import { useT } from '../../lib/i18n';
import { cn } from '../../lib/cn';
import {
  ENTERPRISE_FROM_PER_SEAT,
  ENTERPRISE_MIN_SEATS,
  PLAN_LIMITS,
  PLAN_PRICES,
  type Plan as PlanId,
} from '../../types';
import { Link } from 'react-router-dom';

/**
 * What the leading work-management suites list per seat, monthly billing,
 * August 2026 — the mid tier and the one above it. Named nowhere on the
 * page: the comparison is against "the usual tools", and the figures are
 * what they are.
 */
const USUAL = { team: { label: 'mid tier', monthly: 10, yearly: 7 }, business: { label: 'upper tier', monthly: 19, yearly: 12 } } as const;

const money = (n: number) => (Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`);

const reduced = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * The front door.
 *
 * White paper, near-black type at ordinary weights, neutral greys with no
 * tint, and the scarlet from the mark as the only colour. The page shows the
 * product rather than describing it: every picture is a screenshot of the
 * real application, taken from a seeded example studio, so what a visitor
 * sees is what they get after signing up.
 *
 * It leads with what most teams use (the timesheet, leave, the switches that
 * keep everything else out of sight) and lists the rest plainly. No slogan
 * tiles, no count-up numbers, no labels above every section. The palette is
 * scoped to `.site` so the application's own theme is untouched.
 */

const SECTION = 'mx-auto w-full max-w-[1200px] px-4 sm:px-8';
const H2 = 'text-[30px] font-semibold leading-[1.1] tracking-[-0.02em] sm:text-[40px]';
const BODY = 'text-[17px] leading-[1.55]';

export function Landing() {
  const t = useT();
  const team = PLAN_PRICES.team.monthly!;

  return (
    <div className="site min-h-[100dvh] overflow-x-hidden">
      <SiteHeader />

      <main>
        {/* ----- hero ----- */}
        <section className={cn(SECTION, 'pt-10 sm:pt-16')}>
          <div className="grid gap-8 lg:grid-cols-[7fr_5fr] lg:items-end">
            <h1 className="max-w-[16ch] text-[40px] font-semibold leading-[1.02] tracking-[-0.03em] sm:text-[60px]">
              {t('landing.heroTitle1')}
            </h1>
            <div className="flex flex-col gap-6 lg:pb-2">
              <p className={cn(BODY, 'max-w-[46ch] text-muted')}>{t('landing.heroBody')}</p>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                <Go to="/sign-up">{t('landing.ctaNoCard')}</Go>
                <a href="#pricing" className="text-[16px] underline decoration-line-strong underline-offset-4 hover:decoration-ink">
                  {t('landing.ctaSecondary')}
                </a>
              </div>
              <p className="text-[14px] text-muted">{t('landing.heroNote', { price: money(team), seats: PLAN_LIMITS.free.seats ?? 3 })}</p>
            </div>
          </div>

          <Shot src="/landing/timesheet.jpg" alt={t('landing.shotTimesheet')} width={2880} height={1180} className="mt-12 sm:mt-16" priority />
        </section>

        {/* ----- what most teams use ----- */}
        <section id="product" className={cn(SECTION, 'scroll-mt-[92px] pt-24 sm:pt-32')}>
          <Row
            title={t('landing.timeTitle')}
            body={t('landing.timeBody')}
            bullets={[t('landing.timeB1'), t('landing.timeB2'), t('landing.timeB3')]}
          />
          <Row
            title={t('landing.leaveTitle')}
            body={t('landing.leaveBody')}
            bullets={[t('landing.leaveB1'), t('landing.leaveB2'), t('landing.leaveB3')]}
            shot={<Shot src="/landing/leave.jpg" alt={t('landing.shotLeave')} width={2340} height={504} />}
          />
          <Row
            title={t('landing.setupTitle')}
            body={t('landing.setupBody')}
            bullets={[t('landing.setupB1'), t('landing.setupB2'), t('landing.setupB3')]}
            shot={<Shot src="/landing/features.jpg" alt={t('landing.shotFeatures')} width={1960} height={1120} />}
          />
        </section>

        {/* ----- everything else, listed ----- */}
        <section className={cn(SECTION, 'pt-24 sm:pt-32')}>
          <div className="grid gap-10 lg:grid-cols-[4fr_8fr]">
            <div className="flex flex-col gap-4">
              <h2 className={H2}>{t('landing.moreTitle')}</h2>
              <p className={cn(BODY, 'max-w-[38ch] text-muted')}>{t('landing.moreBody')}</p>
              <Link to="/features" className="text-[16px] underline decoration-line-strong underline-offset-4 hover:decoration-ink">
                {t('landing.gridAllLink')}
              </Link>
            </div>
            <dl className="grid border-t border-ink sm:grid-cols-2">
              {(['Tasks', 'Clients', 'Planning', 'Finance', 'Reports', 'Desktop'] as const).map((k) => (
                <div key={k} className="flex flex-col gap-1.5 border-b border-line py-5 sm:odd:pr-8 sm:even:border-l sm:even:pl-8">
                  <dt className="flex items-baseline justify-between gap-3 text-[17px] font-semibold">
                    {t(`landing.more${k}` as 'landing.moreTasks')}
                    <span className="text-[13px] font-normal text-muted">{t(`landing.more${k}Where` as 'landing.moreTasksWhere')}</span>
                  </dt>
                  <dd className="text-[15px] leading-[1.5] text-muted">{t(`landing.more${k}Body` as 'landing.moreTasksBody')}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        {/* ----- pricing ----- */}
        <section id="pricing" className={cn(SECTION, 'scroll-mt-[92px] pt-24 sm:pt-32')}>
          <h2 className={cn(H2, 'max-w-[20ch]')}>{t('landing.pricingTitle')}</h2>
          <p className={cn(BODY, 'mb-10 mt-4 max-w-[56ch] text-muted')}>{t('landing.pricingBody')}</p>
          <Pricing />
          <p className="mt-8 text-[15px] text-muted">{t('landing.pricingNote')}</p>
          <Calculator />
        </section>

        {/* ----- questions ----- */}
        <section className={cn(SECTION, 'pt-24 sm:pt-32')}>
          <div className="grid gap-10 lg:grid-cols-[4fr_8fr]">
            <h2 className={H2}>{t('landing.faqLabel')}</h2>
            <div className="border-t border-ink">
              {(['1', '2', '3', '4', '5', '6'] as const).map((n) => (
                <details key={n} className="group border-b border-line py-5">
                  <summary className="flex cursor-pointer list-none items-baseline justify-between gap-6 text-[19px] font-semibold leading-[1.3] [&::-webkit-details-marker]:hidden">
                    {t(`landing.q${n}` as 'landing.q1')}
                    <span className="inline-block text-[20px] font-normal text-muted transition-transform duration-base ease-emphasis group-open:rotate-45" aria-hidden>+</span>
                  </summary>
                  <p className={cn(BODY, 'mt-3 max-w-[64ch] text-muted')}>{t(`landing.a${n}` as 'landing.a1')}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ----- who makes it ----- */}
        <section id="story" className={cn(SECTION, 'scroll-mt-[92px] pt-24 sm:pt-32')}>
          <div className="grid gap-10 lg:grid-cols-[4fr_8fr]">
            <h2 className={H2}>{t('landing.storyTitle')}</h2>
            <div className="flex max-w-[62ch] flex-col gap-5">
              <p className={BODY}>{t('landing.storyBody1')}</p>
              <p className={cn(BODY, 'text-muted')}>{t('landing.storyBody2')}</p>
              <p className={cn(BODY, 'text-muted')}>{t('landing.storyBody3')}</p>
              <p className="text-[15px]">{t('landing.storySign')}</p>
            </div>
          </div>
        </section>

        {/* ----- close ----- */}
        <section className={cn(SECTION, 'pb-24 pt-24 sm:pb-32 sm:pt-32')}>
          <div className="flex flex-col items-start gap-6 border-t border-ink pt-10 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex flex-col gap-3">
              <h2 className={cn(H2, 'max-w-[18ch]')}>{t('landing.closeTitle')}</h2>
              <p className={cn(BODY, 'max-w-[48ch] text-muted')}>{t('landing.closeNote')}</p>
            </div>
            <Go to="/sign-up">{t('landing.ctaNoCard')}</Go>
          </div>
        </section>
      </main>

      <footer className={cn(SECTION, 'flex flex-wrap items-center justify-between gap-4 border-t border-line py-6 text-[15px] text-muted')}>
        <span className="flex items-center gap-3">
          <Wordmark height={16} className="text-ink" />
          <span>
            {t('landing.footer')}{' '}
            <a href="https://ipassas.com" target="_blank" rel="noreferrer" className="text-ink hover:underline">
              {t('landing.footerBy')}
            </a>
          </span>
        </span>
        <span className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <Link to="/features" className="hover:text-ink">{t('landing.navFeatures')}</Link>
          {/* A plain link: the comparison pages are static HTML, outside the app. */}
          <a href="/compare" className="hover:text-ink">{t('landing.navCompare')}</a>
          <a href="mailto:hello@trackilero.com" className="hover:text-ink">{t('landing.contact')}</a>
          <Link to="/privacy" className="hover:text-ink">{t('landing.privacy')}</Link>
          <Link to="/refunds" className="hover:text-ink">{t('landing.refunds')}</Link>
          <Link to="/cookies" className="hover:text-ink">{t('landing.cookies')}</Link>
          <span>{t('landing.copyright')}</span>
        </span>
      </footer>
    </div>
  );
}

/**
 * A screenshot of the real application, framed by a hairline and nothing
 * else: no browser chrome drawn around it, no tilt, no glow.
 */
function Shot({ src, alt, width, height, className, priority }: { src: string; alt: string; width: number; height: number; className?: string; priority?: boolean }) {
  return (
    <figure className={cn('overflow-hidden border border-line-strong bg-paper', className)}>
      <img src={src} alt={alt} width={width} height={height} loading={priority ? 'eager' : 'lazy'} decoding="async" className="block h-auto w-full" />
    </figure>
  );
}

/** One thing the product does: words on the left, the screen on the right, or words alone. */
function Row({ title, body, bullets, shot }: { title: string; body: string; bullets: string[]; shot?: ReactNode }) {
  return (
    <div className={cn('grid gap-8 border-t border-line py-12 first:border-ink sm:py-16', shot ? 'lg:grid-cols-[4fr_8fr]' : 'lg:grid-cols-[4fr_8fr]')}>
      <h2 className={H2}>{title}</h2>
      <div className="flex flex-col gap-6">
        <p className={cn(BODY, 'max-w-[60ch]')}>{body}</p>
        <ul className="grid gap-x-8 gap-y-3 sm:grid-cols-3">
          {bullets.map((b) => (
            <li key={b} className="flex gap-2.5 text-[15px] leading-[1.5] text-muted">
              <span className="mt-[7px] h-1.5 w-1.5 shrink-0 bg-spark" aria-hidden />
              {b}
            </li>
          ))}
        </ul>
        {shot}
      </div>
    </div>
  );
}

function Pricing() {
  const t = useT();
  // Monthly leads, because the round figure is the one worth remembering;
  // the yearly toggle is the discount, not the headline.
  const [yearly, setYearly] = useState(false);

  const price = (plan: PlanId) => {
    const p = PLAN_PRICES[plan];
    const value = yearly ? p.yearly : p.monthly;
    if (value === null) return t('landing.custom');
    if (value === 0) return t('landing.free');
    return money(value);
  };
  // The seat line is copy, but the number in it must be the enforced one.
  const seats = (plan: PlanId, copy: string) => {
    const limit = PLAN_LIMITS[plan].seats;
    return limit === null ? copy : copy.replace(/\d+/, String(limit));
  };

  return (
    <>
      <div className="mb-8 inline-flex items-center border border-line-strong p-1 text-[14px]">
        {([false, true] as const).map((value) => (
          <button
            key={String(value)}
            type="button"
            onClick={() => setYearly(value)}
            aria-pressed={yearly === value}
            className={cn('h-8 px-4 transition-colors duration-base ease-emphasis', yearly === value ? 'bg-ink text-on-ink' : 'text-muted hover:text-ink')}
          >
            {t(value ? 'landing.billingYearly' : 'landing.billingMonthly')}
          </button>
        ))}
      </div>

      <div className="grid items-stretch gap-4 md:grid-cols-2 xl:grid-cols-4">
        <PlanCard
          name={t('landing.planFree')}
          price={price('free')}
          bestFor={t('landing.planFreeFor')}
          seats={seats('free', t('landing.planFreeSeats'))}
          items={[t('landing.planFree1'), t('landing.planFree2'), t('landing.planFree3'), t('landing.planFree4')]}
          cta={t('landing.cta')}
          to="/sign-up"
        />
        <PlanCard
          name={t('landing.planTeam')}
          price={price('team')}
          period={t('landing.perSeatMonth')}
          note={yearly ? t('landing.billedYearly') : undefined}
          bestFor={t('landing.planTeamFor')}
          seats={seats('team', t('landing.planTeamSeats'))}
          lead={t('landing.planTeam0')}
          items={[t('landing.planTeam1'), t('landing.planTeam2'), t('landing.planTeam3'), t('landing.planTeam4'), t('landing.planTeam5'), t('landing.planTeam6'), t('landing.planTeam7'), t('landing.planTeam8')]}
          cta={t('landing.ctaTrial')}
          to="/sign-up"
          tag={t('landing.teamTag')}
        />
        <PlanCard
          name={t('landing.planBusiness')}
          price={price('business')}
          period={t('landing.perSeatMonth')}
          note={yearly ? t('landing.billedYearly') : undefined}
          bestFor={t('landing.planBusinessFor')}
          seats={seats('business', t('landing.planBusinessSeats'))}
          lead={t('landing.planBusiness0')}
          items={[t('landing.planBusiness1'), t('landing.planBusiness2'), t('landing.planBusiness3'), t('landing.planBusiness4'), t('landing.planBusiness5'), t('landing.planBusiness6'), t('landing.planBusiness7'), t('landing.planBusiness8')]}
          cta={t('landing.ctaTrial')}
          to="/sign-up"
        />
        <PlanCard
          name={t('landing.planEnterprise')}
          price={`from ${money(ENTERPRISE_FROM_PER_SEAT)}`}
          period={t('landing.perSeatMonth')}
          bestFor={t('landing.planEnterpriseFor')}
          seats={t('landing.planEnterpriseSeats')}
          lead={t('landing.planEnterprise0')}
          items={[t('landing.planEnterprise1'), t('landing.planEnterprise2'), t('landing.planEnterprise3'), t('landing.planEnterprise4')]}
          cta={t('landing.contact')}
          to="mailto:hello@trackilero.com"
        />
      </div>
    </>
  );
}

/** Seats × plan, ours beside the usual tools', so the saving is a number and not a claim. */
function Calculator() {
  const t = useT();
  const [seats, setSeats] = useState(10);
  const [plan, setPlan] = useState<'team' | 'business'>('team');
  const ours = seats * (PLAN_PRICES[plan].monthly ?? 0);
  const theirs = seats * USUAL[plan].monthly;
  const overEnterprise = seats >= ENTERPRISE_MIN_SEATS;
  const oursRef = useRef<HTMLSpanElement>(null);
  const theirsRef = useRef<HTMLSpanElement>(null);

  // The two bills settle onto their new value rather than jumping — a
  // figure arriving, which is the one kind of motion the page allows text.
  useEffect(() => {
    if (reduced()) return;
    const tweens = [
      [oursRef.current, ours],
      [theirsRef.current, theirs],
    ] as const;
    const active = tweens.map(([el, target]) => {
      if (!el) return null;
      const from = { value: Number(el.dataset.value ?? target) };
      el.dataset.value = String(target);
      return gsap.to(from, {
        value: target,
        duration: 0.45,
        ease: 'power2.out',
        onUpdate: () => {
          el.textContent = money(Math.round(from.value * 100) / 100);
        },
      });
    });
    return () => active.forEach((tw) => tw?.kill());
  }, [ours, theirs]);

  return (
    <div className="mt-16 grid gap-8 border-t border-line-strong pt-10 lg:grid-cols-[1fr_2fr]">
      <div className="flex flex-col gap-6">
        <p className="text-[13px] text-muted">{t('landing.calcLabel')}</p>
        <div className="flex flex-col gap-3">
          <span className="text-[32px] font-light leading-none tracking-[-0.02em] tabular-nums">{t('landing.calcSeats', { count: seats })}</span>
          <input
            type="range"
            min={3}
            max={100}
            value={seats}
            onChange={(e) => setSeats(Number(e.target.value))}
            aria-label={t('landing.calcLabel')}
            className="w-full accent-[var(--ink)]"
          />
        </div>
        <div className="inline-flex border border-line-strong p-1 text-[14px] self-start">
          {(['team', 'business'] as const).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPlan(p)}
              aria-pressed={plan === p}
              className={cn('h-8 px-4 transition-colors duration-base ease-emphasis', plan === p ? 'bg-ink text-on-ink' : 'text-muted hover:text-ink')}
            >
              {p === 'team' ? t('landing.planTeam') : t('landing.planBusiness')}
            </button>
          ))}
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-2 border border-ink bg-paper p-6">
          <span className="text-[13px] text-muted">{t('landing.calcUs', { plan: plan === 'team' ? t('landing.planTeam') : t('landing.planBusiness') })}</span>
          <span ref={oursRef} data-value={ours} className="text-[56px] font-light leading-none tracking-[-0.02em] tabular-nums">{money(ours)}</span>
          <span className="text-[15px] text-muted">{t('landing.calcPerMonth')}{overEnterprise ? ` · ${t('landing.planEnterprise')}` : ''}</span>
        </div>
        <div className="flex flex-col gap-2 border border-line-strong p-6">
          <span className="text-[13px] text-muted">{t('landing.calcThem', { plan: USUAL[plan].label })}</span>
          <span ref={theirsRef} data-value={theirs} className="text-[56px] font-light leading-none tracking-[-0.02em] tabular-nums text-muted">{money(theirs)}</span>
          <span className="text-[15px] text-muted">{t('landing.calcPerMonth')}</span>
        </div>
        <p className="text-[22px] font-light tracking-[-0.01em] sm:col-span-2">
          <span className="text-spark">{t('landing.calcSaving', { amount: money(theirs - ours) })}</span>
        </p>
        <p className="text-[13px] text-muted sm:col-span-2">{t('landing.calcNote')}</p>
      </div>
    </div>
  );
}

function PlanCard({ name, price, period, note, bestFor, seats, lead, items, cta, to, tag }: { name: string; price: string; period?: string; note?: string; bestFor: string; seats: string; lead?: string; items: string[]; cta: string; to: string; tag?: string }) {
  const t = useT();
  const external = to.startsWith('mailto:');
  const featured = !!tag;
  const button = cn(
    'inline-flex h-10 w-full items-center justify-center gap-2 px-4 text-center text-[15px] tracking-[-0.01em]',
    'transition-[filter,border-color,transform] duration-base ease-emphasis active:scale-[0.97]',
    featured ? 'bg-go text-on-go hover:brightness-110' : 'border border-line-strong hover:border-ink',
  );

  /**
   * The card is a stack of fixed bands, not a stack of content.
   *
   * Four cards side by side only read as a table if the name, the price, the
   * "best for" line, the seat count and the first tick sit on the same line
   * in each one. Letting each band take the height of its own text puts them
   * all at different heights and the comparison stops being scannable, so
   * every band is given the height of its longest occupant and the list
   * takes whatever is left.
   */
  return (
    <div
      className={cn(
        'flex h-full flex-col border p-6',
        BODY,
        featured ? 'border-ink bg-paper' : 'border-line-strong',
      )}
    >
      <div className="flex h-7 shrink-0 items-center justify-between gap-3">
        <span className="text-muted">{name}</span>
        {tag && <span className="border border-ink px-2 py-0.5 text-[11px] uppercase tracking-[0.04em]">{tag}</span>}
      </div>

      {/* Price and period on one line, with the billing note reserving its
          row whether or not the current toggle has one to show. */}
      <div className="mt-5 flex h-[58px] shrink-0 flex-col justify-start gap-1.5">
        <span className="flex flex-nowrap items-baseline gap-2">
          <span className="text-[36px] font-light leading-none tracking-[-0.02em] tabular-nums">{price}</span>
          {period && (
            <span className="whitespace-nowrap text-[10px] uppercase tracking-[0.04em] text-muted">{period}</span>
          )}
        </span>
        <span className="text-[12.5px] text-faint">{note ?? '\u00a0'}</span>
      </div>

      <div className="mt-5 flex h-[68px] shrink-0 flex-col gap-1 text-[15px]">
        <span className="text-muted">{t('landing.bestFor')}</span>
        <span className="text-pretty">{bestFor}</span>
      </div>

      <div className="mt-4 flex h-[46px] shrink-0 items-start text-[15px] text-muted">
        <span className="text-pretty">{seats}</span>
      </div>

      <ul className="mt-1 flex flex-1 flex-col gap-2.5 border-t border-line pt-5 text-[15px]">
        {lead && <li className="text-muted">{lead}</li>}
        {items.map((item) => (
          <li key={item} className="flex items-start gap-2.5">
            <span className="mt-[3px] shrink-0 text-muted"><Icon name="check" size={13} /></span>
            <span>{item}</span>
          </li>
        ))}
      </ul>

      <div className="mt-6 shrink-0">
        {external ? <a href={to} className={button}>{cta}</a> : <Link to={to} className={button}>{cta}</Link>}
      </div>
    </div>
  );
}
